import { captureException, dedupeIntegration, globalHandlersIntegration, init } from "@sentry/browser";
import type { ErrorEvent } from "@sentry/browser";

export const WEBSITE_RELEASE = "unjabbed-web@2026-10-25-error-context-v2";
const PUBLIC_DSN = "https://cab674c74cc64840ba7feead5365f57a@o4505512089812992.ingest.us.sentry.io/4505519528280064";
const boundaries = new Set(["MAIN_RENDER_ERROR", "ROOT_RENDER_ERROR", "PUSH_MODAL_RENDER_ERROR", "HEADER_RENDER_ERROR", "TOAST_RENDER_ERROR"]);

// Fixed technical categories preserve a useful diagnosis without sending the
// original message, extension identity, page URL or member information.
function errorKind(value: string | undefined) {
  if (value === "Unjabbed monitoring verification") return "verification";
  if (/Minified React error #\d+/.test(value || "")) return "react_render";
  if (["ResizeObserver loop completed with undelivered notifications.", "ResizeObserver loop limit exceeded"].includes(value || "")) return "resize_observer";
  if (["Script error.", "Script error"].includes(value || "")) return "opaque_script";
  if (/^Loading (?:CSS )?chunk \d+ failed\b/.test(value || "")) return "chunk_load";
  if (["Failed to fetch", "NetworkError when attempting to fetch resource.", "Load failed"].includes(value || "")) return "network_request";
  return "unknown";
}

function pageKind() {
  if (typeof window === "undefined") return "unknown";
  const path = window.location.pathname;
  if (path === "/") return "discovery";
  // Never transmit dynamic path segments, query strings or fragments.
  for (const page of ["messages", "notification", "settings", "profile", "auth", "onboarding", "community", "subscription"]) {
    if (path === `/${page}` || path.startsWith(`/${page}/`)) return page;
  }
  return "other";
}

function eventKind(mechanism: string | undefined, handled: boolean | undefined) {
  if (mechanism === "onunhandledrejection" || mechanism === "auto.browser.global_handlers.onunhandledrejection") return "unhandled_rejection";
  return handled === false ? "uncaught_error" : "caught_error";
}

// Construct an allowlisted event. Never transmit page contents, member identities,
// request bodies/headers, navigation history, query strings or arbitrary messages.
export function privateWebsiteEvent(event: ErrorEvent): ErrorEvent {
  const sources = new Set<string>();
  const values = (event.exception?.values || []).map((exception) => {
    const reactCode = exception.value?.match(/Minified React error #(\d+)/)?.[1];
    const frames = exception.stacktrace?.frames?.flatMap((frame) => {
      try {
        const url = new URL(frame.filename || "");
        if (["chrome-extension:", "moz-extension:"].includes(url.protocol)) { sources.add("browser_extension"); return []; }
        if (!["https:", "http:"].includes(url.protocol)) { sources.add("other"); return []; }
        if (!["app.unjabbed.app", "localhost", "127.0.0.1"].includes(url.hostname)) { sources.add("external_script"); return []; }
        if (!/^\/static\/js\/[a-zA-Z0-9._-]+\.js$/.test(url.pathname)) { sources.add("other_app_location"); return []; }
        sources.add("member_bundle");
        const safePosition = (position: number | undefined) => Number.isSafeInteger(position) && (position as number) >= 0 ? position : undefined;
        return [{ filename: `https://app.unjabbed.app${url.pathname}`, lineno: safePosition(frame.lineno), colno: safePosition(frame.colno), in_app: true }];
      } catch { sources.add("other"); return []; }
    });
    const kind = eventKind(exception.mechanism?.type, exception.mechanism?.handled);
    return {
      type: ["Error", "TypeError", "RangeError", "ReferenceError", "SyntaxError", "URIError"].includes(exception.type || "") ? exception.type : "Error",
      value: exception.value === "Unjabbed monitoring verification" ? "Unjabbed monitoring verification" : reactCode ? `Minified React error #${reactCode}` : "Website operation failed",
      ...(frames?.length ? { stacktrace: { frames } } : {}),
      mechanism: { type: kind === "unhandled_rejection" ? "onunhandledrejection" : kind === "uncaught_error" ? "onerror" : "handled", handled: exception.mechanism?.handled !== false },
    };
  });
  const boundary = String(event.tags?.boundary || "");
  const firstException = event.exception?.values?.[0];
  return {
    type: undefined,
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: "javascript",
    level: "error",
    release: WEBSITE_RELEASE,
    environment: event.environment === "verification" ? "verification" : "production",
    exception: { values },
    user: { ip_address: "0.0.0.0" },
    tags: {
      surface: "member_web",
      ...(boundaries.has(boundary) ? { boundary } : {}),
      error_kind: errorKind(firstException?.value),
      event_kind: eventKind(firstException?.mechanism?.type, firstException?.mechanism?.handled),
      error_source: sources.size === 0 ? "no_frames" : sources.size === 1 ? Array.from(sources)[0] : "mixed_sources",
      page: pageKind(),
    },
  };
}

export function reportWebsiteError(error: unknown, boundary: string) {
  try {
    captureException(error, { tags: { boundary: boundaries.has(boundary) ? boundary : "ROOT_RENDER_ERROR" } });
  } catch { /* Monitoring must never interrupt the member's existing fallback. */ }
}

if (process.env.NODE_ENV === "production" && typeof window !== "undefined" &&
    ["app.unjabbed.app", "localhost", "127.0.0.1"].includes(window.location.hostname)) {
  try {
    init({
      dsn: PUBLIC_DSN,
      release: WEBSITE_RELEASE,
      environment: window.location.hostname === "app.unjabbed.app" ? "production" : "verification",
      defaultIntegrations: false,
      integrations: [globalHandlersIntegration(), dedupeIntegration()],
      sendDefaultPii: false,
      sendClientReports: false,
      maxBreadcrumbs: 0,
      beforeSend: privateWebsiteEvent,
    });
  } catch { /* Preserve startup even if the monitoring provider is unavailable. */ }
}
